"use client";

import { useState } from "react";
import { getCachedCountry } from "@/lib/geo";
import {
  PHONE_COUNTRIES,
  composeE164,
  countryOrDefault,
  formatNationalInput,
  parseE164,
  type PhoneCountry,
} from "@/lib/phone-e164";

/**
 * Country-select + tel input composed control emitting canonical E.164.
 *
 * India (the default, ~93% of traffic) keeps today's exact UX: a `+91` prefix
 * beside a 10-digit numeric field. Picking any other country swaps the visible
 * dial code and the digit cap; the value the form receives is always the full
 * E.164 string (`+919876543210`, `+971501234567`) — the shape the backend
 * already stores and WhatsApp already sends to.
 *
 * `value` is the controlled E.164 string. A bare national number (a legacy
 * checkout prefill like `9876543210`) is accepted and interpreted as the
 * selected country, so this drops into existing state without a migration.
 * An empty national part emits `""` — never a bare dial code — so `required`
 * and every existing validator still see "empty" as empty.
 */

/**
 * Country precedence: the value's own dial code (the seeker already told us) >
 * the session geo hint > the `defaultCountry` prop (India).
 *
 * `getCachedCountry()` is synchronous and returns null on the server or before
 * geo resolves, so this never blocks a render and always falls open to India —
 * a wrong guess costs the seeker one tap on the picker, which is why it is safe
 * to guess at all.
 */
function initialCountry(value: string, defaultCountry: string): PhoneCountry {
  const fromValue = value.trim().startsWith("+") ? parseE164(value).country : null;
  return fromValue ?? countryOrDefault(getCachedCountry() ?? defaultCountry);
}

/** The national digits to display, whether `value` is E.164 or a bare number. */
function nationalFromValue(value: string, country: PhoneCountry): string {
  const raw = value.trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    const parsed = parseE164(raw);
    // A dial code the value carries but the picker doesn't match (mid-edit)
    // still shows its digits rather than blanking the field under the user.
    return parsed.country ? parsed.national : raw.replace(/\D/g, "");
  }
  return formatNationalInput(country, raw);
}

export interface PhoneInputE164Props {
  /** Controlled value: canonical E.164, a bare national number, or "". */
  value: string;
  /** Receives the composed E.164 string, or "" when no digits are entered. */
  onChange: (value: string) => void;
  /** ISO2 fallback when the value carries no dial code and no geo hint. */
  defaultCountry?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Applied to the tel input, so callers keep their existing field styling. */
  className?: string;
  /** Applied to the flex row wrapping the picker + input. */
  wrapperClassName?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  /** Accessible name for the country picker (localize per surface). */
  countryLabel?: string;
}

export function PhoneInputE164({
  value,
  onChange,
  defaultCountry = "IN",
  required = false,
  disabled = false,
  id = "phone",
  name = "phone",
  placeholder,
  autoFocus,
  onFocus,
  onBlur,
  className = "input-sacred w-full rounded-r-lg px-4 py-3 text-body text-text-primary",
  wrapperClassName = "flex items-stretch",
  countryLabel = "Country code",
  ...aria
}: PhoneInputE164Props) {
  const [country, setCountry] = useState<PhoneCountry>(() => initialCountry(value, defaultCountry));
  const national = nationalFromValue(value, country);

  function emit(nextCountry: PhoneCountry, nextNational: string) {
    onChange(nextNational ? composeE164(nextCountry.dialCode, nextNational) : "");
  }

  function handleCountryChange(iso2: string) {
    const next = countryOrDefault(iso2);
    setCountry(next);
    // Re-cap the digits to the new country's maximum so switching from a
    // longer country to a shorter one can't leave an over-long number.
    emit(next, formatNationalInput(next, national));
  }

  return (
    <div className={wrapperClassName}>
      <select
        aria-label={countryLabel}
        value={country.iso2}
        disabled={disabled}
        onChange={(e) => handleCountryChange(e.target.value)}
        data-testid="phone-country-select"
        // Semantic role tokens only — no --ish-* aliases, no inline style, no
        // arbitrary type sizes (the design-system ratchet counts all three).
        className="text-body rounded-l-lg border border-r-0 border-border-default bg-surface-canvas px-2 font-medium text-text-muted"
      >
        {PHONE_COUNTRIES.map((c) => (
          <option key={c.iso2} value={c.iso2}>
            {c.flag} {c.dialCode}
          </option>
        ))}
      </select>
      <input
        id={id}
        name={name}
        // Stable hook for the production money-path synthetic. Its label is
        // i18n-owned copy ("WhatsApp mobile number"), and a synthetic that
        // matched that copy by regex silently stopped resolving — the checkout
        // pin then failed 100% of the runs that executed it while nobody could
        // tell a copy edit from a broken checkout. Do not key the pin on copy.
        data-testid="phone-national-input"
        type="tel"
        // `tel` (not `numeric`): the country picker means the seeker may be
        // entering any national format, and the phone keypad is the standard
        // affordance for it. Non-digits are stripped on change regardless.
        inputMode="tel"
        autoComplete="tel-national"
        required={required}
        disabled={disabled}
        value={national}
        onChange={(e) => emit(country, formatNationalInput(country, e.target.value))}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoFocus={autoFocus}
        maxLength={country.nationalDigits.max}
        className={className}
        {...aria}
      />
    </div>
  );
}
